import { toastService } from "cruzo";

export const CONTENT_DOWNLOAD_MAX_ATTEMPTS = 5;

let activeRetryToastId: string | null = null;

function dismissActiveRetryToast(): void {
  if (!activeRetryToastId) {
    return;
  }
  toastService.dismiss(activeRetryToastId);
  activeRetryToastId = null;
}

function showRetryToast(nextAttempt: number, maxAttempts: number): void {
  dismissActiveRetryToast();
  activeRetryToastId = toastService.show({
    kind: "error",
    message: `Повторная попытка (${nextAttempt} из ${maxAttempts})`,
    alignX: "center",
    alignY: "center",
    timeoutMs: 0,
  });
}

function showFinalFailureToast(): void {
  dismissActiveRetryToast();
  activeRetryToastId = toastService.show({
    kind: "error",
    message: "Попробуйте позже",
    alignX: "center",
    alignY: "center",
    timeoutMs: 5000,
  });
}

async function runWithContentDownloadRetry<T>(
  action: () => Promise<T>,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= CONTENT_DOWNLOAD_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 1) {
      dismissActiveRetryToast();
    }

    try {
      const result = await action();
      dismissActiveRetryToast();
      return result;
    } catch (error) {
      lastError = error;
      if (attempt === CONTENT_DOWNLOAD_MAX_ATTEMPTS) {
        showFinalFailureToast();
        throw error;
      }
      showRetryToast(attempt + 1, CONTENT_DOWNLOAD_MAX_ATTEMPTS);
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Content download failed");
}

export async function fetchWithContentRetry(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  return runWithContentDownloadRetry(async () => {
    const response = await fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch content: ${response.status}`);
    }
    return response;
  });
}

export async function fetchJsonWithContentRetry<T = unknown>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetchWithContentRetry(url, init);
  return response.json() as Promise<T>;
}

export async function fetchBlobWithContentRetry(
  url: string,
  init?: RequestInit,
): Promise<Blob> {
  const response = await fetchWithContentRetry(url, init);
  return response.blob();
}
